import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import Button from '@mui/material/Button';
import ButtonBase from '@mui/material/ButtonBase';
import Chip from '@mui/material/Chip';
import Skeleton from '@mui/material/Skeleton';
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { useCallback, useState } from 'react';

import { useDatasheetPopoverStore } from '#components/datasheet-popover/datasheet-popover-store.js';
import { PinnablePreviewTooltip } from '#components/pinnable-preview-tooltip/pinnable-preview-tooltip.js';
import { useTRPC } from '#src/trpc.js';

import type { PinnablePreviewTriggerProps } from '#components/pinnable-preview-tooltip/pinnable-preview-tooltip.js';
import type { PopperPlacementType } from '@mui/material/Popper';
import type { MouseEvent as ReactMouseEvent, ReactNode } from 'react';

const PREVIEW_FALLBACK_PLACEMENTS: readonly PopperPlacementType[] = ['bottom', 'right', 'left'];

type PdfCitationLocator =
	| { elementIndexes: readonly number[]; pageNumber?: number; citedText?: string }
	| { elementIndexes?: undefined; pageNumber: number; citedText?: string }
	| { elementIndexes?: undefined; pageNumber?: number; citedText: string };

export type PdfCitationAppearance = 'inline' | 'underlined' | 'chip' | 'location';

export type PdfCitationProps = PdfCitationLocator & {
	/** File-store identifier for the cited PDF. */
	fileId: string;
	/** The exact visible phrase supported by the citation. */
	children: ReactNode;
	/** Optional context shown only in the preview. */
	description?: string;
	/**
	 * Generic trailing slot rendered inside `chip` and `location` appearances. It is intentionally omitted from
	 * `inline` and `underlined` appearances so prose citations keep their existing inline flow.
	 */
	endAdornment?: ReactNode;
	/**
	 * Visual and label treatment: `inline` and `underlined` both show supplied text under a dotted underline,
	 * `chip` shows supplied text in a PDF chip, and `location` shows a compact locator in a PDF chip.
	 */
	appearance?: PdfCitationAppearance;
	className?: string;
};

/**
 * An inline, inspectable PDF citation. The source preview is requested only after
 * the citation is first opened, while the cited phrase remains ordinary inline text.
 */
export function PdfCitation({
	fileId,
	elementIndexes,
	pageNumber,
	citedText,
	description,
	endAdornment,
	appearance = 'inline',
	children,
	className,
}: PdfCitationProps) {
	const [hasOpened, setHasOpened] = useState(false);
	const [failedImage, setFailedImage] = useState<string | null>(null);
	const markOpened = useCallback(() => setHasOpened(true), []);
	const openDatasheet = useDatasheetPopoverStore((state) => state.open);
	const trpc = useTRPC();
	const normalizedCitedText = citedText?.trim();
	const renderRequest =
		elementIndexes !== undefined && elementIndexes.length > 0 ?
			{ fileId, elementIndexes: [...elementIndexes], renderMode: 'crop' as const }
		: normalizedCitedText ?
			{
				fileId,
				citedText: normalizedCitedText,
				...(pageNumber === undefined ? {} : { pageNumbers: [pageNumber] }),
				renderMode: 'crop' as const,
			}
		:	{ fileId, pageNumbers: [pageNumber!], renderMode: 'page' as const };

	const previewQuery = useQuery(
		trpc.file.renderPdfElements.queryOptions(renderRequest, {
			enabled: hasOpened,
			gcTime: Infinity,
			staleTime: Infinity,
			throwOnError: false,
		}),
	);
	const citedElementIndexes = elementIndexes ?? [];
	// A union crop cannot span pages, so when no render covers every cited element the
	// first one identifies the page whose render to fall back to.
	const [primaryElementIndex] = citedElementIndexes;
	const preview =
		primaryElementIndex !== undefined ?
			(previewQuery.data?.renders.find((render) =>
				citedElementIndexes.every((elementIndex) => render.elementIndexes.includes(elementIndex)),
			) ?? previewQuery.data?.renders.find((render) => render.elementIndexes.includes(primaryElementIndex)))
		: normalizedCitedText ? previewQuery.data?.renders[0]
		: previewQuery.data?.renders.find((render) => render.pageNumber === pageNumber);
	const citedElementText = preview?.elements
		.map(({ text }) => text)
		.filter((text): text is string => text !== null && text !== '')
		.join('\n');
	const triggerLabel = appearance === 'location' ? getPdfCitationLocationLabel(pageNumber) : children;

	const handleOpenDatasheet = (event: ReactMouseEvent<HTMLButtonElement>, close: () => void) => {
		const resolvedPageNumber = preview?.pageNumber ?? pageNumber;
		close();
		openDatasheet(
			{
				fileId,
				initialPageNumber: resolvedPageNumber,
				initialBoundingBoxes: preview?.elements.map(({ boundingBox }) => ({
					page: preview.pageNumber,
					...boundingBox,
				})),
			},
			event.currentTarget.ownerDocument,
		);
	};

	const imageSource = preview?.base64Image ? `data:image/png;base64,${preview.base64Image}` : undefined;
	const showImage = imageSource !== undefined && failedImage !== imageSource;
	const requiresResolvedPage = elementIndexes !== undefined || pageNumber === undefined;
	const isDatasheetActionDisabled = requiresResolvedPage && previewQuery.isPending;
	const renderTitle = (close: () => void) => {
		const datasheetButton = (
			<Button
				variant='outlined'
				startIcon={<PictureAsPdfIcon />}
				disabled={isDatasheetActionDisabled}
				onClick={(event) => handleOpenDatasheet(event, close)}
			>
				<span>Datasheet</span>
			</Button>
		);

		return (
			<div className='flex max-w-full flex-col gap-2'>
				{normalizedCitedText && (
					<div className='max-w-[70ch]'>
						<p className='mt-1 max-h-28 overflow-auto whitespace-pre-wrap text-gray-12 italic'>
							&quot;{normalizedCitedText}&quot;
						</p>
					</div>
				)}
				{previewQuery.isPending ?
					<PdfCitationPreviewSkeleton datasheetButton={datasheetButton} />
				: preview ?
					<>
						{description !== undefined && description.trim() !== '' && (
							<p className='max-w-[70ch] text-pretty'>{description}</p>
						)}
						{showImage ?
							<img
								src={imageSource}
								alt={`PDF citation source on page ${preview.pageNumber}`}
								onError={() => setFailedImage(imageSource)}
								className='max-h-96 w-auto max-w-full rounded-md bg-white object-contain'
							/>
						: citedElementText ?
							<pre className='max-h-72 max-w-[70ch] overflow-auto font-mono whitespace-pre-wrap text-gray-11'>
								{citedElementText}
							</pre>
						:	<p className='text-gray-11'>Source preview unavailable.</p>}
						<div className='flex items-center justify-between gap-3'>
							<span className='font-mono text-gray-11'>Page {preview.pageNumber}</span>
							{datasheetButton}
						</div>
					</>
				:	<>
						<p className='text-gray-11'>Source preview unavailable.</p>
						<div className='flex justify-end'>{datasheetButton}</div>
					</>
				}
			</div>
		);
	};
	const renderTrigger = (triggerProps: PinnablePreviewTriggerProps) =>
		appearance === 'chip' || appearance === 'location' ?
			<Chip
				{...triggerProps}
				role='button'
				tabIndex={0}
				aria-label='View PDF citation'
				icon={<PictureAsPdfIcon aria-hidden />}
				label={
					endAdornment === undefined ? triggerLabel : (
						<span className='inline-flex min-w-0 items-center gap-0.5'>
							<span className='truncate min-w-0'>{triggerLabel}</span>
							{endAdornment}
						</span>
					)
				}
				size='small'
				variant='outlined'
				className={clsx(
					'cursor-pointer !rounded-sm !border-gray-a6 !bg-transparent align-baseline !text-gray-11 !shadow-none',
					'hover:!border-gray-a8 hover:!bg-transparent hover:!text-gray-12',
					'focus-visible:!outline-1 focus-visible:!outline-offset-2 focus-visible:!outline-blue-9',
					'[&_.MuiChip-icon]:!size-3.5 [&_.MuiChip-icon]:!text-gray-9',
					className,
				)}
			/>
		:	<ButtonBase
				{...triggerProps}
				component='span'
				role='button'
				tabIndex={0}
				aria-label='View PDF citation'
				style={{ display: 'inline' }}
				className={clsx(
					'cursor-pointer rounded-sm align-baseline text-inherit [font:inherit]',
					'focus-visible:outline-1 focus-visible:outline-offset-2 focus-visible:outline-blue-9',
					// `text-decoration-style: dotted` packs the dots tight and gives no control over their spacing, so
					// the underline is a repeating dot tile instead. The tile hangs off the bottom of the inline box,
					// so the global `text-box` trim has to be off here: engines that honour the trim on inline boxes
					// put that edge on the alphabetic baseline and the dots then cut through descenders. `ButtonBase`
					// zeroes padding from the `mui` cascade layer, registered after Tailwind's `utilities` layer, so
					// the tile's offset must be important.
					'bg-[radial-gradient(circle_at_center,var(--color-gray-a10)_0.5px,transparent_0.5px)]',
					'bg-[length:5px_1px] bg-bottom bg-repeat-x !pb-[1px] [text-box:normal]',
					className,
				)}
			>
				{triggerLabel}
			</ButtonBase>;

	return (
		<PinnablePreviewTooltip title={renderTitle} fallbackPlacements={PREVIEW_FALLBACK_PLACEMENTS} onOpen={markOpened}>
			{renderTrigger}
		</PinnablePreviewTooltip>
	);
}

/** Formats the page-only source locator used by location-style citation triggers. */
function getPdfCitationLocationLabel(pageNumber: number | undefined): string {
	return pageNumber === undefined ? 'datasheet' : `p. ${pageNumber}`;
}

function PdfCitationPreviewSkeleton({ datasheetButton }: { datasheetButton: ReactNode }) {
	return (
		<div aria-label='Loading PDF citation preview' className='flex w-80 max-w-full flex-col gap-2'>
			<Skeleton variant='rounded' height={160} />
			<div className='flex items-center justify-between gap-3'>
				<Skeleton width={72} />
				{datasheetButton}
			</div>
		</div>
	);
}
